import React, { useEffect, useMemo, useState } from 'react';
import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import TextField from '@mui/material/TextField';
import MenuItem from '@mui/material/MenuItem';
import Chip from '@mui/material/Chip';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import Alert from '@mui/material/Alert';
import CircularProgress from '@mui/material/CircularProgress';
import Switch from '@mui/material/Switch';
import FormControlLabel from '@mui/material/FormControlLabel';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Divider from '@mui/material/Divider';
import AddIcon from '@mui/icons-material/Add';
import CloseIcon from '@mui/icons-material/Close';
import CloudIcon from '@mui/icons-material/CloudDownload';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { productionKeys } from './queryKeys';
import {
  getPressConfig,
  getSeparationProfile,
  patchSeparationProfile,
  upsertSeparationProfile,
  upsertSeparationUnit,
  deleteSeparationUnit,
  extractSeparationsFromPdf,
} from './productionApi';
import type {
  PressConfig,
  RawSeparation,
  SeparationProfile,
  SeparationRole,
  SeparationScopeType,
  SeparationUnit,
  SeparationUnitKind,
} from './productionTypes';

interface Props {
  profileId: number | null;
  press: string;
  onClose: () => void;
}

const ROLE_COLOURS: Record<SeparationRole, string> = {
  process: '#475569',
  spot: '#9333ea',
  coating: '#0ea5e9',
  special_ink: '#ea580c',
  technical: '#94a3b8',
  ignored: '#cbd5e1',
};

// Black is B, not the CMYK K. 'K' stays as a lookup key only so a legacy profile
// still resolves its hex — new profiles are seeded as B (see below).
const PROCESS_COLOUR_HINTS: Record<string, string> = {
  B: '#0f172a',
  C: '#06b6d4',
  M: '#ec4899',
  Y: '#facc15',
  K: '#0f172a',
  CYAN: '#06b6d4',
  MAGENTA: '#ec4899',
  YELLOW: '#facc15',
  BLACK: '#0f172a',
};

function defaultProcessSeparations(): RawSeparation[] {
  return [
    { name: 'B (Black)', kind: 'process', pdf_index: 1, hex_colour: PROCESS_COLOUR_HINTS.B },
    { name: 'C (Cyan)', kind: 'process', pdf_index: 2, hex_colour: PROCESS_COLOUR_HINTS.C },
    { name: 'M (Magenta)', kind: 'process', pdf_index: 3, hex_colour: PROCESS_COLOUR_HINTS.M },
    { name: 'Y (Yellow)', kind: 'process', pdf_index: 4, hex_colour: PROCESS_COLOUR_HINTS.Y },
  ];
}

// Role-only chips for substrate templates: drop one onto a slot to lock the
// slot's role (e.g. "any spot in unit 5") without naming a specific Pantone.
// The artwork's actual sep gets matched in at render time.
const ROLE_CHIPS: { kind: SeparationRole; label: string }[] = [
  { kind: 'spot', label: 'Any spot' },
  { kind: 'coating', label: 'Any coating' },
  { kind: 'special_ink', label: 'Any special' },
];

function roleChipSeparation(kind: SeparationRole): RawSeparation {
  // Empty name is the sentinel that tells handleAssignToUnit / the backend
  // to persist the slot as role-only (separation_name = null).
  return { name: '', kind, pdf_index: 0, hex_colour: null };
}

const ROLE_DISPLAY_LABEL: Record<SeparationRole, string> = {
  process: 'Process',
  spot: 'Any spot',
  coating: 'Any coating',
  special_ink: 'Any special',
  technical: 'Any technical',
  ignored: 'Ignored',
};

interface ScopeFormState {
  scope_type: SeparationScopeType;
  customer_code: string;
  customer_ref: string;
  stock_code: string;
  substrate: string;
}

function emptyScope(): ScopeFormState {
  return {
    scope_type: 'default',
    customer_code: '',
    customer_ref: '',
    stock_code: '',
    substrate: '',
  };
}

export default function SeparationProfileEditor({ profileId, press, onClose }: Props): JSX.Element {
  const queryClient = useQueryClient();
  const isCreate = profileId === null;

  const pressConfigQuery = useQuery({
    queryKey: productionKeys.pressConfig(),
    queryFn: () => getPressConfig(press).then((p) => [p] as PressConfig[]),
  });
  // pendingProfileId is the source of truth for "what profile is the editor
  // currently showing" — set immediately on first save when creating new,
  // pre-set from the prop when editing existing. Driving the query off it
  // means the create flow refreshes correctly after the implicit save.
  const [pendingProfileId, setPendingProfileId] = useState<number | null>(profileId);
  const profileQuery = useQuery({
    queryKey: productionKeys.separationProfile(pendingProfileId ?? -1),
    queryFn: () => getSeparationProfile(pendingProfileId as number),
    enabled: pendingProfileId !== null,
  });

  const config = pressConfigQuery.data?.[0];
  const profile: SeparationProfile | undefined = profileQuery.data;

  const [scope, setScope] = useState<ScopeFormState>(emptyScope);
  const [approved, setApproved] = useState(false);
  const [notes, setNotes] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [palette, setPalette] = useState<RawSeparation[]>(defaultProcessSeparations);
  const [newSepDraft, setNewSepDraft] = useState<{ name: string; kind: SeparationRole; hex: string }>(
    { name: '', kind: 'spot', hex: '' },
  );
  const [extractDialog, setExtractDialog] = useState<{ open: boolean; pdfPath: string }>({
    open: false,
    pdfPath: '',
  });

  // Hydrate scope/notes/approved from loaded profile.
  useEffect(() => {
    if (!profile) return;
    setApproved(profile.approved);
    setNotes(profile.notes ?? '');
    const next = emptyScope();
    next.scope_type = profile.scope_type;
    if (profile.scope_type === 'customer') {
      next.customer_code = profile.scope_value ?? '';
    } else if (profile.scope_type === 'stock_code') {
      next.stock_code = profile.scope_value ?? '';
    } else if (profile.scope_type === 'substrate') {
      next.substrate = profile.scope_value ?? '';
    } else if (profile.scope_type === 'artwork') {
      const [c, r] = (profile.scope_value ?? '').split('|');
      next.customer_code = c ?? '';
      next.customer_ref = r ?? '';
    }
    setScope(next);
    setPendingProfileId(profile.id);
    // Seed palette with the named units on the profile so they're available
    // as drag sources too — without this an existing assignment can't be
    // moved to a different slot. Role-only slots (separation_name=null)
    // aren't draggable items; they live in the ROLE_CHIPS strip instead.
    if (profile.units.length > 0) {
      setPalette((prev) => {
        const next = [...prev];
        for (const u of profile.units) {
          if (!u.separation_name) continue;
          if (!next.find((s) => s.name === u.separation_name)) {
            next.push({
              name: u.separation_name,
              kind: u.separation_role,
              pdf_index: 0,
              hex_colour: u.hex_colour,
            });
          }
        }
        return next;
      });
    }
  }, [profile]);

  // All four backend endpoints return the updated profile in the response —
  // we prime the single-profile cache directly so the editor reflects the
  // new state without waiting for a refetch (which is what was causing the
  // close-and-reopen workaround). The list query still gets invalidated so
  // /production/sequences picks up the change too.
  const cacheUpdatedProfile = (saved: SeparationProfile) => {
    queryClient.setQueryData(productionKeys.separationProfile(saved.id), saved);
    queryClient.invalidateQueries({ queryKey: ['production', 'separation-profiles'] });
  };
  const upsertProfileMutation = useMutation({
    mutationFn: upsertSeparationProfile,
    onError: (e: any) => setError(e?.response?.data?.error ?? e?.message ?? 'Save failed'),
    onSuccess: (saved) => {
      setPendingProfileId(saved.id);
      cacheUpdatedProfile(saved);
    },
  });
  const patchProfileMutation = useMutation({
    mutationFn: ({ id, patch }: { id: number; patch: Parameters<typeof patchSeparationProfile>[1] }) =>
      patchSeparationProfile(id, patch),
    onError: (e: any) => setError(e?.response?.data?.error ?? e?.message ?? 'Update failed'),
    onSuccess: cacheUpdatedProfile,
  });
  const upsertUnitMutation = useMutation({
    mutationFn: ({ id, unit }: { id: number; unit: SeparationUnit }) =>
      upsertSeparationUnit(id, unit),
    onError: (e: any) => setError(e?.response?.data?.error ?? e?.message ?? 'Unit update failed'),
    onSuccess: cacheUpdatedProfile,
  });
  const deleteUnitMutation = useMutation({
    mutationFn: ({
      id,
      kind,
      index,
    }: {
      id: number;
      kind: SeparationUnitKind;
      index: number;
    }) => deleteSeparationUnit(id, kind, index),
    onError: (e: any) => setError(e?.response?.data?.error ?? e?.message ?? 'Unit clear failed'),
    onSuccess: cacheUpdatedProfile,
  });
  const extractMutation = useMutation({
    mutationFn: extractSeparationsFromPdf,
    onError: (e: any) =>
      setError(e?.response?.data?.error ?? e?.message ?? 'Cloudflow extraction failed'),
    onSuccess: (resp) => {
      // The extraction created/updated a profile server-side and pre-
      // populated unit assignments. Switch the editor to that profile so
      // the operator sees the Cloudflow proposal.
      setPendingProfileId(resp.profile.id);
      setApproved(resp.profile.approved);
      setNotes(resp.profile.notes ?? '');
      // Seed the palette with everything Cloudflow returned so any items
      // the auto-classifier ignored are still draggable into a slot.
      setPalette((prev) => {
        const next = [...prev];
        for (const sep of resp.raw_separations) {
          if (!next.find((s) => s.name === sep.name)) {
            next.push({
              name: sep.name,
              kind: (sep.kind as SeparationRole) || 'spot',
              pdf_index: sep.pdf_index,
              hex_colour: sep.hex_colour,
            });
          }
        }
        return next;
      });
      queryClient.invalidateQueries({ queryKey: ['production', 'separation-profiles'] });
      queryClient.invalidateQueries({
        queryKey: productionKeys.separationProfile(resp.profile.id),
      });
      setExtractDialog({ open: false, pdfPath: '' });
    },
  });

  const ensureProfileSaved = async (): Promise<number | null> => {
    if (pendingProfileId !== null) return pendingProfileId;
    setError(null);
    let scope_value: string | null = null;
    if (scope.scope_type === 'customer') {
      scope_value = scope.customer_code.trim();
      if (!scope_value) {
        setError('Customer code is required for customer scope.');
        return null;
      }
    } else if (scope.scope_type === 'stock_code') {
      scope_value = scope.stock_code.trim();
      if (!scope_value) {
        setError('Stock code is required for stock_code scope.');
        return null;
      }
    } else if (scope.scope_type === 'substrate') {
      scope_value = scope.substrate.trim();
      if (!scope_value) {
        setError('Substrate prefix is required for substrate scope.');
        return null;
      }
    } else if (scope.scope_type === 'artwork') {
      if (!scope.customer_code.trim() || !scope.customer_ref.trim()) {
        setError('Customer code and customer ref are both required for artwork scope.');
        return null;
      }
      scope_value = `${scope.customer_code.trim()}|${scope.customer_ref.trim()}`;
    }
    try {
      const saved = await upsertProfileMutation.mutateAsync({
        press_code: press,
        scope_type: scope.scope_type,
        scope_value,
        approved,
        notes: notes || null,
      });
      return saved.id;
    } catch {
      return null;
    }
  };

  const handleAssignToUnit = async (
    sep: RawSeparation,
    kind: SeparationUnitKind,
    index: number,
  ) => {
    const id = await ensureProfileSaved();
    if (id === null) return;
    upsertUnitMutation.mutate({
      id,
      unit: {
        unit_kind: kind,
        unit_index: index,
        // Empty name = role-only template slot; backend persists as NULL.
        separation_name: sep.name.trim() || null,
        separation_role: sep.kind,
        hex_colour: sep.hex_colour ?? null,
      },
    });
  };

  const handleClearUnit = (kind: SeparationUnitKind, index: number) => {
    if (pendingProfileId === null) return;
    deleteUnitMutation.mutate({ id: pendingProfileId, kind, index });
  };

  const handleApplyDefaults = async () => {
    const id = await ensureProfileSaved();
    if (id === null || !config) return;
    const defaults = defaultProcessSeparations();
    for (let i = 0; i < defaults.length && i < config.ink_units; i++) {
      await upsertUnitMutation.mutateAsync({
        id,
        unit: {
          unit_kind: 'ink',
          unit_index: i + 1,
          separation_name: defaults[i].name,
          separation_role: 'process',
          hex_colour: defaults[i].hex_colour ?? null,
        },
      });
    }
  };

  const handleSaveAndClose = async () => {
    const id = await ensureProfileSaved();
    if (id === null) return;
    await patchProfileMutation.mutateAsync({ id, patch: { approved, notes: notes || null } });
    onClose();
  };

  const handleAddSeparation = () => {
    const name = newSepDraft.name.trim();
    if (!name) return;
    setPalette((prev) =>
      prev.find((s) => s.name === name)
        ? prev
        : [
            ...prev,
            {
              name,
              kind: newSepDraft.kind,
              pdf_index: 0,
              hex_colour: newSepDraft.hex.trim() || null,
            },
          ],
    );
    setNewSepDraft({ name: '', kind: 'spot', hex: '' });
  };

  // The unit grid renders against the freshest profile units (post-mutation
  // refetch). For unsaved drafts, the grid is empty until the first save.
  const liveProfile: SeparationProfile | null =
    profile && pendingProfileId === profile.id ? profile : null;

  const findUnit = (kind: SeparationUnitKind, index: number): SeparationUnit | undefined =>
    liveProfile?.units.find((u) => u.unit_kind === kind && u.unit_index === index);

  const isLoading =
    pressConfigQuery.isLoading || (!isCreate && profileQuery.isLoading) || !config;

  return (
    <>
    <Dialog open onClose={onClose} fullWidth maxWidth="lg" data-testid="sequence-editor">
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <Box sx={{ flex: 1 }}>
          {isCreate ? 'New separation profile' : `Edit profile`}{' '}
          <Typography component="span" variant="body2" sx={{ color: 'text.secondary' }}>
            on {press}
          </Typography>
        </Box>
        <IconButton size="small" onClick={onClose} aria-label="Close">
          <CloseIcon fontSize="small" />
        </IconButton>
      </DialogTitle>
      <DialogContent dividers sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
        {error && (
          <Alert severity="error" onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        {/* Scope */}
        <Box>
          <Typography variant="overline" sx={{ color: 'text.secondary', letterSpacing: 1 }}>
            Scope
          </Typography>
          <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} sx={{ mt: 1 }}>
            <TextField
              select
              label="Scope type"
              value={scope.scope_type}
              onChange={(e) =>
                setScope({
                  ...scope,
                  scope_type: e.target.value as SeparationScopeType,
                })
              }
              size="small"
              sx={{ minWidth: 160 }}
              disabled={!isCreate}
              data-testid="scope-type-select"
            >
              <MenuItem value="default">Default</MenuItem>
              <MenuItem value="customer">Client (customer)</MenuItem>
              <MenuItem value="substrate">Raw material (substrate)</MenuItem>
              <MenuItem value="stock_code">Stock code</MenuItem>
              <MenuItem value="artwork">SKU (customer + ref)</MenuItem>
            </TextField>
            {(scope.scope_type === 'customer' || scope.scope_type === 'artwork') && (
              <TextField
                label="Customer code"
                value={scope.customer_code}
                onChange={(e) =>
                  setScope({ ...scope, customer_code: e.target.value.toUpperCase() })
                }
                size="small"
                disabled={!isCreate}
                sx={{ maxWidth: 200 }}
              />
            )}
            {scope.scope_type === 'artwork' && (
              <TextField
                label="Customer ref (SKU)"
                value={scope.customer_ref}
                onChange={(e) => setScope({ ...scope, customer_ref: e.target.value })}
                size="small"
                disabled={!isCreate}
                fullWidth
              />
            )}
            {scope.scope_type === 'stock_code' && (
              <TextField
                label="Stock code (e.g. RC660-752X931F)"
                value={scope.stock_code}
                onChange={(e) => setScope({ ...scope, stock_code: e.target.value })}
                size="small"
                disabled={!isCreate}
                fullWidth
              />
            )}
            {scope.scope_type === 'substrate' && (
              <TextField
                label="Substrate prefix (e.g. RC660)"
                value={scope.substrate}
                onChange={(e) =>
                  setScope({ ...scope, substrate: e.target.value.toUpperCase() })
                }
                size="small"
                disabled={!isCreate}
                sx={{ maxWidth: 240 }}
              />
            )}
          </Stack>
          <Typography variant="caption" sx={{ color: 'text.secondary', mt: 1, display: 'block' }}>
            Resolution priority (highest first):{' '}
            <strong>SKU → stock code → raw material → client → default</strong>
          </Typography>
        </Box>

        <Divider />

        {/* Body: separations + unit grid */}
        {isLoading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
            <CircularProgress size={24} />
          </Box>
        ) : (
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', md: '320px 1fr' },
              gap: 3,
              alignItems: 'start',
            }}
          >
            {/* Separation palette (drag source) */}
            <Box>
              <Stack direction="row" alignItems="center" spacing={1}>
                <Typography variant="overline" sx={{ color: 'text.secondary', letterSpacing: 1 }}>
                  Separations
                </Typography>
                <Tooltip
                  title={
                    scope.scope_type !== 'artwork'
                      ? 'Cloudflow extraction creates a SKU/artwork-scope profile — switch scope to SKU to use it.'
                      : !scope.customer_code.trim() || !scope.customer_ref.trim()
                      ? 'Fill in customer code + customer ref first.'
                      : 'Pull separations from a PDF on Cloudflow'
                  }
                >
                  <span style={{ marginLeft: 'auto' }}>
                    <Button
                      size="small"
                      startIcon={<CloudIcon />}
                      disabled={
                        scope.scope_type !== 'artwork' ||
                        !scope.customer_code.trim() ||
                        !scope.customer_ref.trim()
                      }
                      onClick={() => setExtractDialog({ open: true, pdfPath: '' })}
                    >
                      Extract from Cloudflow…
                    </Button>
                  </span>
                </Tooltip>
              </Stack>
              <Stack spacing={0.5} sx={{ mt: 1 }}>
                {palette.map((sep) => (
                  <PaletteRow key={sep.name} sep={sep} />
                ))}
              </Stack>
              {/* Role-only chips — drag onto a slot for substrate-template
                  "any spot in unit 5" entries. The named sep gets matched
                  in from the artwork at render time. */}
              <Typography
                variant="caption"
                sx={{ color: 'text.secondary', display: 'block', mt: 2, mb: 0.5 }}
              >
                Role placeholders
              </Typography>
              <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
                {ROLE_CHIPS.map((rc) => (
                  <RoleChip key={rc.kind} kind={rc.kind} label={rc.label} />
                ))}
              </Stack>
              <Stack direction="row" spacing={1} sx={{ mt: 2 }}>
                <TextField
                  size="small"
                  label="Add separation"
                  value={newSepDraft.name}
                  onChange={(e) => setNewSepDraft({ ...newSepDraft, name: e.target.value })}
                  fullWidth
                />
                <TextField
                  select
                  size="small"
                  label="Role"
                  value={newSepDraft.kind}
                  onChange={(e) =>
                    setNewSepDraft({ ...newSepDraft, kind: e.target.value as SeparationRole })
                  }
                  sx={{ minWidth: 120 }}
                >
                  <MenuItem value="process">Process</MenuItem>
                  <MenuItem value="spot">Spot</MenuItem>
                  <MenuItem value="coating">Coating</MenuItem>
                  <MenuItem value="special_ink">Special</MenuItem>
                  <MenuItem value="technical">Technical</MenuItem>
                  <MenuItem value="ignored">Ignored</MenuItem>
                </TextField>
                <IconButton
                  size="small"
                  aria-label="Add separation"
                  onClick={handleAddSeparation}
                  disabled={!newSepDraft.name.trim()}
                >
                  <AddIcon fontSize="small" />
                </IconButton>
              </Stack>
              <TextField
                size="small"
                label="Hex (optional)"
                placeholder="#00AEEF"
                value={newSepDraft.hex}
                onChange={(e) => setNewSepDraft({ ...newSepDraft, hex: e.target.value })}
                sx={{ mt: 1, width: '100%' }}
              />
              <Button
                size="small"
                variant="outlined"
                onClick={handleApplyDefaults}
                sx={{ mt: 2 }}
                disabled={upsertUnitMutation.isPending || upsertProfileMutation.isPending}
              >
                Apply process defaults (B,C,M,Y)
              </Button>
            </Box>

            {/* Unit grid (drop targets) */}
            <Box>
              <Typography variant="overline" sx={{ color: 'text.secondary', letterSpacing: 1 }}>
                Press units
              </Typography>
              {pendingProfileId === null && (
                <Alert severity="info" sx={{ mt: 1, mb: 2 }}>
                  Drop a separation onto a unit to save the profile and assign the slot.
                </Alert>
              )}
              <Box sx={{ mt: 1 }}>
                <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                  Ink units
                </Typography>
                <Box
                  sx={{
                    display: 'grid',
                    gridTemplateColumns: `repeat(${config.ink_units}, 1fr)`,
                    gap: 1,
                    mt: 0.5,
                  }}
                >
                  {Array.from({ length: config.ink_units }, (_, i) => i + 1).map((idx) => (
                    <UnitSlot
                      key={`ink-${idx}`}
                      kind="ink"
                      index={idx}
                      assigned={findUnit('ink', idx)}
                      onDrop={(sep) => handleAssignToUnit(sep, 'ink', idx)}
                      onClear={() => handleClearUnit('ink', idx)}
                    />
                  ))}
                </Box>
              </Box>
              {config.coater_units > 0 && (
                <Box sx={{ mt: 3 }}>
                  <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                    Coaters
                  </Typography>
                  <Box
                    sx={{
                      display: 'grid',
                      gridTemplateColumns: `repeat(${config.coater_units}, 1fr)`,
                      gap: 1,
                      mt: 0.5,
                      maxWidth: 240,
                    }}
                  >
                    {Array.from({ length: config.coater_units }, (_, i) => i + 1).map((idx) => (
                      <UnitSlot
                        key={`coater-${idx}`}
                        kind="coater"
                        index={idx}
                        assigned={findUnit('coater', idx)}
                        onDrop={(sep) => handleAssignToUnit(sep, 'coater', idx)}
                        onClear={() => handleClearUnit('coater', idx)}
                      />
                    ))}
                  </Box>
                </Box>
              )}
            </Box>
          </Box>
        )}

        <Divider />

        <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
          <FormControlLabel
            control={
              <Switch checked={approved} onChange={(_, v) => setApproved(v)} />
            }
            label="Approved"
          />
          <TextField
            label="Notes"
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            size="small"
            fullWidth
            multiline
            minRows={1}
          />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button
          variant="contained"
          onClick={handleSaveAndClose}
          disabled={
            upsertProfileMutation.isPending ||
            patchProfileMutation.isPending ||
            upsertUnitMutation.isPending
          }
        >
          Save
        </Button>
      </DialogActions>
    </Dialog>

    <Dialog
      open={extractDialog.open}
      onClose={() =>
        !extractMutation.isPending && setExtractDialog({ open: false, pdfPath: '' })
      }
      maxWidth="sm"
      fullWidth
      data-testid="extract-dialog"
    >
      <DialogTitle>Extract separations from Cloudflow</DialogTitle>
      <DialogContent dividers sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        <Typography variant="body2" sx={{ color: 'text.secondary' }}>
          Paste the Cloudflow PDF path for{' '}
          <strong>
            {scope.customer_code}/{scope.customer_ref}
          </strong>
          . The extractor reads the colorant list (separation name, type, and
          RGB) and proposes a unit assignment you can drag-fix.
        </Typography>
        <TextField
          autoFocus
          size="small"
          fullWidth
          placeholder="cloudflow://PP_FILE_STORE/Customer/file.pdf"
          value={extractDialog.pdfPath}
          onChange={(e) =>
            setExtractDialog((s) => ({ ...s, pdfPath: e.target.value }))
          }
          disabled={extractMutation.isPending}
          InputProps={{ sx: { fontFamily: 'ui-monospace, monospace', fontSize: 13 } }}
        />
        {extractMutation.isPending && (
          <Stack direction="row" spacing={1} alignItems="center">
            <CircularProgress size={16} />
            <Typography variant="caption" sx={{ color: 'text.secondary' }}>
              Asking Cloudflow for the colorant list…
            </Typography>
          </Stack>
        )}
      </DialogContent>
      <DialogActions>
        <Button
          onClick={() => setExtractDialog({ open: false, pdfPath: '' })}
          disabled={extractMutation.isPending}
        >
          Cancel
        </Button>
        <Button
          variant="contained"
          startIcon={<CloudIcon />}
          disabled={!extractDialog.pdfPath.trim() || extractMutation.isPending}
          onClick={() =>
            extractMutation.mutate({
              press_code: press,
              pdf_path: extractDialog.pdfPath.trim(),
              customer_code: scope.customer_code.trim(),
              customer_ref: scope.customer_ref.trim(),
            })
          }
        >
          Extract
        </Button>
      </DialogActions>
    </Dialog>
    </>
  );
}

function RoleChip({ kind, label }: { kind: SeparationRole; label: string }): JSX.Element {
  const swatch = ROLE_COLOURS[kind];
  return (
    <Box
      draggable
      onDragStart={(e) => {
        e.dataTransfer.setData('application/json', JSON.stringify(roleChipSeparation(kind)));
        e.dataTransfer.effectAllowed = 'copy';
      }}
      sx={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 0.75,
        px: 1,
        py: 0.5,
        borderRadius: 999,
        border: '1px dashed',
        borderColor: swatch,
        bgcolor: 'background.paper',
        cursor: 'grab',
        '&:hover': { bgcolor: 'action.hover' },
        '&:active': { cursor: 'grabbing' },
      }}
    >
      <Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: swatch }} />
      <Typography sx={{ fontSize: 11, fontWeight: 600, color: 'text.secondary' }}>
        {label}
      </Typography>
    </Box>
  );
}

function PaletteRow({ sep }: { sep: RawSeparation }): JSX.Element {
  const swatch = sep.hex_colour || ROLE_COLOURS[sep.kind];
  return (
    <Box
      draggable
      onDragStart={(e) => {
        e.dataTransfer.setData('application/json', JSON.stringify(sep));
        e.dataTransfer.effectAllowed = 'copy';
      }}
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 1.25,
        p: 1,
        borderRadius: 1,
        border: '1px solid',
        borderColor: 'divider',
        cursor: 'grab',
        bgcolor: 'background.paper',
        '&:hover': { bgcolor: 'action.hover' },
        '&:active': { cursor: 'grabbing' },
      }}
    >
      <Box sx={{ width: 16, height: 16, borderRadius: '4px', bgcolor: swatch, flexShrink: 0 }} />
      <Typography sx={{ fontSize: 13, fontWeight: 500, flex: 1, minWidth: 0 }} noWrap>
        {sep.name}
      </Typography>
      <Chip size="small" label={sep.kind} variant="outlined" sx={{ height: 18, fontSize: 10 }} />
    </Box>
  );
}

function UnitSlot({
  kind,
  index,
  assigned,
  onDrop,
  onClear,
}: {
  kind: SeparationUnitKind;
  index: number;
  assigned: SeparationUnit | undefined;
  onDrop: (sep: RawSeparation) => void;
  onClear: () => void;
}): JSX.Element {
  const [dragOver, setDragOver] = useState(false);
  // Role-only spot slots get a distinct empty-square + "PMS" treatment so
  // operators can spot at a glance "this slot is reserved for any spot".
  const isSpotPlaceholder =
    !!assigned && assigned.separation_role === 'spot' && !assigned.separation_name;
  const swatch = assigned
    ? assigned.hex_colour || ROLE_COLOURS[assigned.separation_role]
    : null;
  return (
    <Box
      data-testid={`unit-slot-${kind}-${index}`}
      onDragOver={(e) => {
        e.preventDefault();
        e.dataTransfer.dropEffect = 'copy';
        if (!dragOver) setDragOver(true);
      }}
      onDragLeave={() => setDragOver(false)}
      onDrop={(e) => {
        e.preventDefault();
        setDragOver(false);
        try {
          const raw = e.dataTransfer.getData('application/json');
          if (!raw) return;
          const sep = JSON.parse(raw) as RawSeparation;
          onDrop(sep);
        } catch {
          /* ignore */
        }
      }}
      sx={{
        position: 'relative',
        height: 64,
        borderRadius: 1.5,
        border: isSpotPlaceholder
          ? '1.5px solid rgba(0,0,0,0.45)'
          : '2px dashed',
        borderColor: isSpotPlaceholder
          ? undefined
          : dragOver ? 'primary.main' : assigned ? 'transparent' : 'divider',
        bgcolor: isSpotPlaceholder
          ? 'transparent'
          : assigned ? swatch : dragOver ? 'rgba(203,96,21,0.08)' : 'background.paper',
        color: isSpotPlaceholder ? 'common.black' : assigned ? 'white' : 'text.secondary',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        textAlign: 'center',
        px: 0.5,
        transition: 'border-color 100ms, background-color 100ms',
      }}
    >
      <Typography
        sx={{
          fontSize: 10,
          fontWeight: 700,
          letterSpacing: '0.06em',
          textTransform: 'uppercase',
          opacity: assigned ? 0.9 : 0.7,
        }}
      >
        {kind === 'ink' ? `Unit ${index}` : `Coater ${index}`}
      </Typography>
      {assigned ? (
        <>
          <Typography
            sx={{
              fontSize: isSpotPlaceholder ? 14 : 12,
              fontWeight: 700,
              lineHeight: 1.2,
              maxWidth: '100%',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
              fontStyle: assigned.separation_name ? 'normal' : isSpotPlaceholder ? 'normal' : 'italic',
              opacity: assigned.separation_name || isSpotPlaceholder ? 1 : 0.85,
              letterSpacing: isSpotPlaceholder ? '0.08em' : 'normal',
            }}
          >
            {isSpotPlaceholder
              ? 'PMS'
              : assigned.separation_name || ROLE_DISPLAY_LABEL[assigned.separation_role]}
          </Typography>
          <IconButton
            size="small"
            aria-label={`Clear ${kind} ${index}`}
            onClick={onClear}
            sx={{
              position: 'absolute',
              top: 2,
              right: 2,
              color: isSpotPlaceholder ? 'rgba(0,0,0,0.55)' : 'rgba(255,255,255,0.85)',
              p: 0.25,
            }}
          >
            <CloseIcon sx={{ fontSize: 14 }} />
          </IconButton>
        </>
      ) : (
        <Typography sx={{ fontSize: 11, mt: 0.25, opacity: 0.7 }}>empty</Typography>
      )}
    </Box>
  );
}
